import styled from '@emotion/styled';
import * as React from 'react';
import { Textarea } from '../../../components/ContactForm/Textarea';
import { ArrowDownIcon } from '../../../components/Icons/ArrowDownIcon';
import { AttentionIcon } from '../../../components/Icons/AttentionIcon';
import { NativeSelect } from '../../../components/NativeSelect';
import { RequestFormButtonsV2 } from '../../../components/RequestForm/RequestFormButtonsV2';
import { TrackComponent } from '../../../components/TrackComponent';
import * as vars from '../../../consts/variables';
import { C1AU_DOMAIN, CRUISE_AWAY_DOMAIN } from '../../../context/markets';
import { __ } from '../../../helpers/TranslationService';
import { specialNewsletterTextCruiseNids } from '../../../helpers/requestFormHelpers';
import { validateEmail } from '../../../helpers/validation';
import { ICruiseReducer } from '../../../interfaces';
import { IRequestFormReducer } from '../../../interfaces/IRequestForm';
import { IPageDataReducer } from '../../../reducers/pageData';
import { submitRequestForm } from '../../../reducers/requestForm/requestFormMappers';
import { IRouterReducer } from '../../../reducers/router';
import { DatePickerSelect } from '../components/DatePickerSelect';
import { CTABanner } from './CTABanner';
import { Input } from './Input';
import { NewsletterSubscription } from './NewsletterSubscription';
import { PhoneSelection } from './PhoneSelection';
import { SubmitButton } from './SubmitButton';
import { featureFlagEnabled } from '../../../helpers/featureFlagEnabled';

const styles: Record<string, React.CSSProperties> = {
    wrapper: {
        marginTop: 25,
        display: 'flex',
        flexDirection: 'column',
    },
    iconWrapper: {
        display: 'flex',
        alignItems: 'center',
        margin: '0 15px',
        marginBottom: 20,
    },
    warningIcon: {
        fill: vars.pink,
        marginRight: 5,
    },
    phoneErrorText: {
        fontSize: 14,
        color: vars.pink,
        margin: '-15px 15px 15px 15px',
    },
    errorText: {
        fontSize: 14,
        color: vars.pink,
        marginTop: 1,
    },
    disclaimerText: {
        textAlign: 'center',
        marginBottom: 15,
    },
    streetSection: {
        display: 'flex',
    },
    streetName: {
        flex: 2,
    },
    streetNumber: {
        flex: 1,
    },
    streetNameWrapper: {
        margin: '0 0 15px 15px',
    },
    citySection: {
        display: 'flex',
        margin: '0 15px', //margin:'0 15px'
    },
    zip: {
        flex: 1,
    },
    city: {
        flex: 2,
    },
    cityWrapper: {
        margin: '0 15px 15px', // '0 0 15px 15px'
    },
    cityWrapperRU: {
        margin: '0 0 15px 15px',
    },
    zipWrapper: {
        margin: '0 0 15px 0',
    },
};

export const ContactViaEmailSection = styled.label`
    margin: 0 15px 20px;
    display: flex;
    align-items: center;

    span {
        font-size: 14px;
        font-weight: 400;
        margin-left: 8px;
    }

    input {
        width: 18px;
        height: 18px;
        margin: 0;
    }
`;

const StyledTextarea = styled(Input)`
    margin: 0 15px 15px 0;

    textarea {
        width: 100%;
    }
`;

export const LegalTextContainer = styled.div`
    margin: 0 15px 30px;
    font-size: 12px;
    > a {
        color: ${({ theme }) => theme.link.primary};
    }
`;
interface IProps {
    requestForm: IRequestFormReducer;
    cruisePage: ICruiseReducer;
    router: IRouterReducer;
    pageData: IPageDataReducer;
    onSendRequestClick(requestType?: string): void;
    updatePersonalDataForm(keyAndValue: { key; value }): void;
    isLowTotalBookingValue?: boolean;
}

const CollapseTitle = styled.span<{ isOpen: boolean }>`
    font-size: 14px;
    line-height: 22px;
    font-weight: 600;
    color: ${({ theme }) => theme.link.primary};
    display: flex;
    gap: 6px;
    align-items: center;
    margin: 0 15px 30px;
    ${({ isOpen }) => (isOpen ? 'margin-bottom: 8px;' : '')}
`;

const StyledArrowDownIcon = styled(ArrowDownIcon)<{ isOpen: boolean }>`
    fill: ${({ theme }) => theme.link.primary};
    height: 16px;
    width: 16px;
    transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0)')};
    transition: all 0.2s ease-in-out;
`;

const SHOW_CALLBACK_TIME = false;
type IState = { phoneNumber: string; isCollapseOpen: boolean };

export class PersonalDataForm extends React.Component<IProps, IState> {
    constructor(props) {
        super(props);

        this.state = { phoneNumber: '', isCollapseOpen: false };
    }

    callbackTimeSelectOptions = [
        { id: 0, label: __('personalData.callbackTime.morning', 'dreamlines') },
        { id: 1, label: __('personalData.callbackTime.noon', 'dreamlines') },
        { id: 2, label: __('personalData.callbackTime.afternoon', 'dreamlines') },
        { id: 3, label: __('personalData.callbackTime.evening', 'dreamlines') },
        { id: 4, label: __('personalData.callbackTime.flexible', 'dreamlines') },
    ];

    handleGenderSelectChange = (id: number) => {
        let isFemale = null;
        if (id !== null) {
            isFemale = Boolean(id);
        }
        this.props.updatePersonalDataForm({
            key: 'isFemale',
            value: isFemale,
        });
    };

    handleInputChange = (key) => (event) => {
        this.props.updatePersonalDataForm({
            key,
            value: event.target.value,
        });
    };

    handleDatePickerChange = (key) => (date: string) => {
        this.props.updatePersonalDataForm({
            key,
            value: date,
        });
    };

    handleCallbackTimeChange = (id: number) => {
        const selectedItem = this.callbackTimeSelectOptions.find((item) => id === item.id);
        const value = selectedItem ? selectedItem.label : null;

        this.props.updatePersonalDataForm({
            key: 'callbackTime',
            value,
        });
    };

    handlePhoneChange = (event) => {
        let phone = event.target.value;
        if (phone) {
            phone = phone.replace(/[^\d+]+/g, '');
        }
        if (this.props.requestForm.phone === phone) {
            return;
        }
        this.props.updatePersonalDataForm({
            key: 'phone',
            value: phone,
        });
    };

    handleCountryCodeChange = (value) => {
        this.props.updatePersonalDataForm({
            key: 'countryCode',
            value,
        });

        this.props.updatePersonalDataForm({
            key: 'phoneCountryCode',
            value: value === '-1' ? '' : value,
        });
    };

    handleNewsletterSubscriptionChange = (event) =>
        this.props.updatePersonalDataForm({
            key: 'newsletterSubscription',
            value: event.target.checked,
        });

    handleContactViaEmailChange = (event) =>
        this.props.updatePersonalDataForm({
            key: 'isContactViaEmailOnly',
            value: event.target.checked,
        });

    render() {
        const { requestForm, pageData, cruisePage } = this.props;
        const { mobileRequestFormAdditionalFields, featureFlags } = pageData;
        const {
            isSubmitted,
            isSubmitError,
            firstInvalidFormField,
            newsletterSubscription,
        } = requestForm;

        const showEmailOnlyContactCheckbox = featureFlagEnabled(
            featureFlags,
            'onlyEmailContactCheckbox',
        );

        const genderSelectOptions = [
            { id: 0, label: __('personalData.male', 'dreamlines') },
            { id: 1, label: __('personalData.female', 'dreamlines') },
        ];

        let selectedGender = -1;
        if (requestForm.isFemale !== null) {
            selectedGender = requestForm.isFemale ? 1 : 0;
        }
        const genderSelectProps = {
            selectedId: selectedGender,
            options: genderSelectOptions,
            onChange: this.handleGenderSelectChange,
            defaultOption: ' ',
            isError: isSubmitted && selectedGender === -1,
            isFirstInvalidFormField: firstInvalidFormField === 'isFemale',
            testSelectorName: 'genderSelect',
        };
        const firstnameProps = {
            placeholder: __('mobile.personalData.firstname', 'dreamlines'),
            value: requestForm.firstname,
            onChange: this.handleInputChange('firstname'),
            isError: isSubmitted && requestForm.firstname.length === 0,
            isFirstInvalidFormField: firstInvalidFormField === 'firstname',
            testSelectorName: 'firstname',
        };
        const lastnameProps = {
            placeholder: __('mobile.personalData.lastname', 'dreamlines'),
            value: requestForm.lastname,
            onChange: this.handleInputChange('lastname'),
            isError: isSubmitted && requestForm.lastname.length === 0,
            isFirstInvalidFormField: firstInvalidFormField === 'lastname',
            testSelectorName: 'lastname',
        };
        const phoneSelectionProps = {
            phone: requestForm.phone,
            ddd: requestForm.ddd,
            countryCode: requestForm.countryCode,
            countryCodes: pageData.brandConfig.requestForm.countryCodes,
            isSubmitted,
            onPhoneChange: this.handlePhoneChange,
            onDDDChange: this.handleInputChange('ddd'),
            onCountryCodeChange: this.handleCountryCodeChange,
            portalId: pageData.appConfig.portalId,
            firstInvalidFormField: firstInvalidFormField,
            testSelectorName: 'phone',
        };

        let selectedCallbackTimeId = -1;
        if (requestForm.callbackTime !== null) {
            const selectedItem = this.callbackTimeSelectOptions.find(
                (item) => requestForm.callbackTime === item.label,
            );
            selectedCallbackTimeId = selectedItem ? selectedItem.id : -1;
        }
        const callbackTimeSelectProps = {
            selectedId: selectedCallbackTimeId,
            options: this.callbackTimeSelectOptions,
            onChange: this.handleCallbackTimeChange,
            defaultOption: __('personalData.desiredCallbackTime', 'dreamlines'),
            isError:
                requestForm.callbackTimeRequired && isSubmitted && selectedCallbackTimeId === -1,
            isFirstInvalidFormField: firstInvalidFormField === 'callbackTime',
            testSelectorName: 'callbackTime',
            wrapperStyle: styles.callbackTime,
            className: 'callbackTime',
        };

        const isEmailError = isSubmitted && !validateEmail(requestForm.email);
        const emailProps = {
            placeholder: __('mobile.personalData.email', 'dreamlines'),
            value: requestForm.email,
            onChange: this.handleInputChange('email'),
            type: 'email',
            isError: isEmailError,
            isFirstInvalidFormField: firstInvalidFormField === 'email',
            testSelectorName: 'email',
        };

        const newsletterTextKey =
            specialNewsletterTextCruiseNids.indexOf(this.props.cruisePage.nid) > -1
                ? 'cruiseData.newsletterSubSpecial'
                : 'cruiseData.newsletterSub';
        const newsletterSubscriptionProps = {
            checked: newsletterSubscription,
            onChange: this.handleNewsletterSubscriptionChange,
            text: __(newsletterTextKey, 'dreamlines'),
        };
        const streetNameProps = {
            placeholder: __('mobile.personalData.streetName', 'dreamlines'),
            value: requestForm.streetName,
            onChange: this.handleInputChange('streetName'),
            isError:
                isSubmitted &&
                requestForm.streetName.length === 0 &&
                mobileRequestFormAdditionalFields,
            isFirstInvalidFormField: firstInvalidFormField === 'streetName',
            wrapperStyle: styles.streetNameWrapper,
            testSelectorName: 'streetName',
        };
        const streetNumberProps = {
            placeholder: __('mobile.personalData.streetNumber', 'dreamlines'),
            value: requestForm.streetNumber,
            onChange: this.handleInputChange('streetNumber'),
            isError:
                isSubmitted &&
                requestForm.streetNumber.length === 0 &&
                mobileRequestFormAdditionalFields,
            isFirstInvalidFormField: firstInvalidFormField === 'streetNumber',
            testSelectorName: 'streetNumber',
        };
        const birthdayProps = {
            wrapperStyle: styles.birthday,
            placeholder: __('mobile.personalData.birthday', 'dreamlines'),
            value: requestForm.birthday,
            isMax: true,
            onChange: this.handleDatePickerChange('birthday'),
            isError:
                isSubmitted && (!requestForm.birthday || isNaN(requestForm.birthday.getTime())),
            isFirstInvalidFormField: firstInvalidFormField === 'birthday',
            testSelectorName: 'birthday',
        };

        const messageProps = {
            placeholder: __('personalData.messageOptional', 'dreamlines'),
            value: requestForm.message,
            onChange: this.handleInputChange('message'),
            rows: 3,
        };

        const shouldShowBPG = pageData.appConfig.countryCode === 'de';
        const isRequestFormValid = submitRequestForm(requestForm, mobileRequestFormAdditionalFields)
            .isValid;

        const hasApiValidationErrors =
            requestForm.apiValidationErrors && requestForm.apiValidationErrors.length;

        const TrackableRequestNowButton = TrackComponent(SubmitButton);
        const isAustralianMarket =
            pageData.appConfig.portalId === CRUISE_AWAY_DOMAIN ||
            pageData.appConfig.portalId === C1AU_DOMAIN;

        return (
            <div style={styles.wrapper}>
                <NativeSelect
                    {...genderSelectProps}
                    muiStyling
                    title={__('mobile.personalData.salutation', 'dreamlines')}
                />
                <Input muiStyling {...firstnameProps} />
                <Input muiStyling {...lastnameProps} />
                {mobileRequestFormAdditionalFields ? (
                    <>
                        <div className="birthdaySectionWrapper">
                            <DatePickerSelect {...birthdayProps} />
                        </div>
                        <div className="streetSectionWrapper">
                            <div style={styles.streetSection}>
                                <div style={styles.streetName}>
                                    <Input {...streetNameProps} />
                                </div>
                                <div style={styles.streetNumber}>
                                    <Input {...streetNumberProps} />
                                </div>
                            </div>
                        </div>
                    </>
                ) : null}
                <Input muiStyling {...emailProps} />

                {isEmailError && (
                    <div style={styles.phoneErrorText}>{__('errors.email', 'dreamilines')}</div>
                )}

                {showEmailOnlyContactCheckbox && (
                    <ContactViaEmailSection htmlFor="isContactViaEmailOnly">
                        <input
                            id="isContactViaEmailOnly"
                            name="isContactViaEmailOnly"
                            type="checkbox"
                            checked={requestForm.isContactViaEmailOnly}
                            onChange={this.handleContactViaEmailChange}
                        />
                        <span style={styles.contactViaEmailLabel}>
                            {__('contactViaEmailOnly', 'dreamlines')}
                        </span>
                    </ContactViaEmailSection>
                )}

                <PhoneSelection muiStyling {...phoneSelectionProps} />
                {SHOW_CALLBACK_TIME && <NativeSelect {...callbackTimeSelectProps} />}
                <CollapseTitle
                    isOpen={this.state.isCollapseOpen}
                    onClick={() =>
                        this.setState((state) => ({
                            ...state,
                            isCollapseOpen: !state.isCollapseOpen,
                        }))
                    }
                >
                    {__('anyRequests', 'dreamilines')}
                    <StyledArrowDownIcon isOpen={this.state.isCollapseOpen} />
                </CollapseTitle>
                {this.state.isCollapseOpen && (
                    <StyledTextarea muiStyling type="textarea" rows={6} {...messageProps} />
                )}

                {isSubmitError && (
                    <div style={styles.iconWrapper}>
                        <AttentionIcon width={18} height={18} style={styles.warningIcon} />

                        {hasApiValidationErrors ? (
                            <span style={styles.errorText}>
                                {__(requestForm.apiValidationErrors[0].errorKey, 'dreamlines')}
                            </span>
                        ) : (
                            <span style={styles.errorText}>
                                {__('mobile.errors.networkError', 'dreamlines')}
                            </span>
                        )}
                    </div>
                )}

                {isSubmitted && !isRequestFormValid && (
                    <div style={styles.iconWrapper}>
                        <AttentionIcon width={18} height={18} style={styles.warningIcon} />
                        <span style={styles.errorText}>
                            {__('mobile.errors.validationError', 'dreamlines')}
                        </span>
                    </div>
                )}

                {requestForm.showNewsletterCheckbox && (
                    <NewsletterSubscription {...newsletterSubscriptionProps} />
                )}

                <TrackableRequestNowButton
                    testSelectorName="submitInquiry"
                    onClick={() => this.props.onSendRequestClick()}
                    newsletterSubscription={newsletterSubscription}
                    isRequestFormValid={isRequestFormValid}
                />

                <LegalTextContainer
                    dangerouslySetInnerHTML={{
                        __html: __('requestPage.legalText', 'dreamlines'),
                    }}
                />

                {isAustralianMarket && (
                    <div
                        className="requestFormContainer__disclaimer-text"
                        style={{
                            margin: '0 15px 30px',
                            fontSize: '12px',
                        }}
                    >
                        {__('priceDisclaimer', 'dreamlines')}
                    </div>
                )}
                {shouldShowBPG && <CTABanner />}
                {pageData.appConfig.portalId === 'dreamlines.com' && (
                    <div
                        className="html-with-links"
                        style={styles.disclaimerText}
                        dangerouslySetInnerHTML={{
                            __html: __('request.termsAndPrivacyMessage', 'dreamlines'),
                        }}
                    />
                )}
            </div>
        );
    }
}
